import { useEffect, useRef, useState } from 'react'
import { useStore, useStoreApi } from '@xyflow/react'
import type { CanvasOptionsBarPlacement } from '../optionsBar'
import { OPTIONS_BAR_OFFSET } from '../optionsBar/CanvasOptionsBar.types'

/**
 * Wie viel Platz die **Options-Leiste** über dem Zettel braucht: ihre Höhe
 * (34px) plus der Abstand zum Zettel (`OPTIONS_BAR_OFFSET`, 40px — Platz für
 * den oberen **Anlegepunkt**) und ein wenig Luft zum Rand.
 */
const BAR_ROOM = 34 + OPTIONS_BAR_OFFSET + 14

/**
 * Ob genau EIN Element ausgewählt ist.
 *
 * Ab zwei gehört die Leiste dem **Auswahlrahmen** (CONTEXT.md,
 * „Auswahlrahmen"), und die einzelnen weichen.
 *
 * **Als Boolean aus dem Store und nicht über `data` hereingereicht:** Der
 * Node-Abgleich in `CanvasFlow` vergleicht nur `note`, `startEditing` und
 * `editingEnabled` — ein neues Datenfeld bliebe in alten Nodes stehen. Ein
 * Selektor, der einen Boolean liefert, rendert nur beim Umschlagen neu.
 *
 * **`enabled` ist kein Beiwerk, sondern der Grund, warum das bezahlbar ist.**
 * Der Selektor läuft bei JEDER Store-Änderung — und React Flow schreibt beim
 * Ziehen in jedem Frame. Zählte jeder Zettel dabei alle Zettel durch, wäre das
 * quadratisch in der Zahl der Elemente, Frame für Frame. Die Antwort braucht
 * aber nur, wer selbst ausgewählt ist: Alle anderen zeigen ohnehin keine
 * Leiste.
 */
export function useSoloSelected(enabled: boolean): boolean {
  return useStore(state => {
    if (!enabled) return false
    let count = 0
    for (const node of state.nodeLookup.values()) {
      if (node.selected) {
        count++
        if (count > 1) return false
      }
    }
    return count === 1
  })
}

/**
 * Ob die Leiste über oder unter den Zettel gehört.
 *
 * Klebt er am oberen Rand des Ausschnitts, schwebte sie außerhalb des Bildes —
 * React Flow dreht `Position.Top` nicht von selbst. Dann klappt sie darunter,
 * und ihre Aufkläpper gehen mit.
 *
 * @param topY    die obere Kante des Elements in Canvas-Koordinaten
 * @param enabled nur für den ausgewählten Zettel rechnen (siehe
 *                {@link useSoloSelected})
 */
export function useOptionsBarPlacement(topY: number, enabled: boolean): CanvasOptionsBarPlacement {
  // Ein Boolean als Auswahlergebnis, kein Zahlenpaar: Sonst renderte jeder
  // Zettel bei jedem Zoomschritt und jedem Pixel Verschiebung neu.
  const flip = useStore(state => {
    if (!enabled) return false
    const [, offsetY, zoom] = state.transform
    return topY * zoom + offsetY < BAR_ROOM
  })
  return flip ? 'below' : 'above'
}

/**
 * Wie lange nach der letzten Bewegung des Ausschnitts noch „in Bewegung" gilt.
 *
 * Ein Zug über das Trackpad kommt als Schauer einzelner Schritte; ohne diese
 * Nachlaufzeit erschiene die Leiste zwischen zwei Fingerbewegungen ständig
 * neu. 200 ms sind lang genug, dass ein zusammenhängender Zug einer bleibt,
 * und kurz genug, dass sie unmittelbar nach dem Loslassen wieder da ist.
 */
const PAN_IDLE = 200

/**
 * Wie lange die Leiste nach dem Loslassen noch wegbleibt.
 *
 * Länger als `PAN_IDLE`, und mit Absicht: Wer einen Zettel absetzt und
 * gleich noch einmal nachschiebt, soll dabei keine Leiste unter dem Zeiger
 * auftauchen sehen — die Wartezeit ist die Gelegenheit dazu. Ein zweites
 * Anfassen innerhalb der Frist löscht sie und beginnt von vorn.
 */
const RELEASE_IDLE = 400

/*
  Ob der Zeiger GERADE unten ist — für alle Instanzen gemeinsam.

  Der Lauscher unten hängt nur an ausgewählten Zetteln. Der häufigste Zug
  beginnt aber an einem NICHT ausgewählten: Angefasst, direkt gezogen — React
  Flow wählt ihn beim Zugbeginn aus, erst dann kommt der Lauscher dazu und hat
  das `pointerdown` verpasst. Die Leiste stand während des ganzen Zugs. Dieser
  eine Zähler am `window` (Aufnahmephase, damit ihn kein `stopPropagation`
  unterwegs abfängt) sagt dem Hook beim Einschalten, wo der Zeiger steht.
  Angelegt beim ersten Hook, abgebaut mit dem letzten.
*/
let pointerHeld = false
let pointerTrackers = 0
const onWindowPointerDown = () => { pointerHeld = true }
const onWindowPointerUp = () => { pointerHeld = false }
function trackPointer(): () => void {
  if (pointerTrackers++ === 0) {
    window.addEventListener('pointerdown', onWindowPointerDown, true)
    window.addEventListener('pointerup', onWindowPointerUp, true)
    window.addEventListener('pointercancel', onWindowPointerUp, true)
  }
  return () => {
    if (--pointerTrackers === 0) {
      window.removeEventListener('pointerdown', onWindowPointerDown, true)
      window.removeEventListener('pointerup', onWindowPointerUp, true)
      window.removeEventListener('pointercancel', onWindowPointerUp, true)
      pointerHeld = false
    }
  }
}

/**
 * Ob gerade eine Geste läuft — verschieben, skalieren, **Platz tauschen**,
 * Aufziehen eines Auswahlrechtecks, Fahren des Ausschnitts.
 *
 * Solange sie läuft, blendet die **Options-Leiste** ab: Während einer Geste
 * gehört das Feld der Rückmeldung — Hilfslinie, Maßlinie, Auswahlrahmen —, und
 * die Leiste hat dort nichts zu entscheiden. Sie führe außerdem über die
 * Nachbarn und läge genau dort, wo beim Skalieren die Maßlinie erscheint.
 *
 * **Ein Pointer-Druck im Canvas genügt als Merkmal**, statt jede Geste einzeln zu
 * melden. Ein Klick auf die Leiste selbst zählt nicht — ihr
 * `stopPropagation` erreicht diesen nativen Lauscher NICHT (React hängt am
 * Wurzelknoten), deshalb prüft er das Ziel selbst.
 *
 * **Das Fahren des Ausschnitts zählt mit, und ohne es wäre die Regel halb.**
 * Der Canvas läuft mit `panOnScroll`: Den **Ausschnitt** fährt man über das
 * Trackpad, also ganz ohne Pointer-Druck. Die Leiste blieb dabei stehen und
 * wurde in jedem Frame neu gesetzt — mitsamt ihrem Rückwand-Weichzeichner.
 * Gehört wird die Bewegung selbst (React Flows `transform`), nicht das Rad;
 * warum, steht im Rumpf.
 *
 * **Nur wer eine Leiste zeigt, horcht.** Sonst hinge an jedem Zettel ein
 * Lauscher, und jeder Pointer-Druck setzte in JEDEM von ihnen Zustand — bei
 * fünfzig Zetteln fünfzig Rendervorgänge beim Anfassen und noch einmal
 * fünfzig beim Loslassen, jeder davon mit einer neuen Suche nach der
 * Schriftgröße.
 */
export function useCanvasPointerBusy(enabled: boolean): boolean {
  const domNode = useStore(state => state.domNode)
  const storeApi = useStoreApi()
  const [busy, setBusy] = useState(false)
  const idleRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  /*
    Der AUSWAHLRAHMEN (Rubber-Band) — exakt aus React Flows Store, nicht vom
    Zeiger abgeleitet: `userSelectionRect` steht, solange einer aufgezogen
    wird. Ein Zettel wird dabei MITTEN in der Geste ausgewählt, der Hook geht
    an — und der Zeigerzähler am `window` sagte in der App trotzdem „nicht
    unten" (nachgemessen: `pointerBusy: false` während des Ziehens). Über den
    Store gilt es in jedem Render, ohne Effekt und ohne Nachziehen. Nur für
    Eingeschaltete gelesen — sonst rendert jeder Zettel bei jedem Rahmen.
  */
  const rubberBand = useStore(state => enabled && Boolean(state.userSelectionRect))
  // Ob gerade eine Geste läuft, die beim Loslassen die Wartezeit auslöst —
  // ein Pointer-Druck auf dem Canvas oder ein Auswahlrahmen. Ein Klick auf die
  // Leiste selbst ist keine: Sein `pointerup` darf sie nicht ausblenden.
  const gestureRef = useRef(false)
  /*
    Ob die laufende Geste ein RAHMEN war. Nach ihm gibt es keine Wartezeit:
    Die gilt dem Nachschieben eines gerade abgesetzten Zettels — ein Rahmen
    setzt nichts ab, und die Gruppen-Leiste soll so schnell da sein wie die
    eines einfach angeklickten Zettels.
  */
  const rubberBandRef = useRef(false)
  useEffect(() => {
    if (rubberBand) rubberBandRef.current = true
  }, [rubberBand])

  /*
    Das Einschalten WÄHREND des Renderns erkennen, nicht erst im Effekt: Beim
    Aufziehen eines Auswahlrahmens wird ein Zettel mitten in der Geste
    ausgewählt, der Hook geht an — und meldete für einen Render „frei", bis
    der Effekt nachzog. Die Leiste blitzte auf und blendete dann 220 ms lang
    ab. Mit dem Umschlag im Render ist sie in keinem Frame zu sehen.
  */
  const [wasEnabled, setWasEnabled] = useState(enabled)
  /*
    **Und der Umschlag gilt SOFORT, in genau diesem Durchgang.** `setBusy`
    verwirft zwar den laufenden Render und lässt ihn neu laufen — aber der
    Rumpf ist bis dahin einmal durchgelaufen, und was er dabei an
    NACHGELAGERTEN Zustand geschrieben hat, überlebt das Verwerfen. Genau das
    tat `usePresence` am Zettel: Es sah in diesem einen Durchgang „Leiste da",
    merkte es sich, und im nächsten Durchgang war die Leiste schon wieder weg
    — also blendete sie 220 ms lang ab. Auf dem Schirm: ein Aufblitzen beim
    Griff an den **Tauschpunkt** eines noch nicht ausgewählten Zettels
    (gemeldet 2026-09-08). Der zurückgegebene Wert darf deshalb nicht auf den
    nächsten Durchgang warten.
  */
  let held = busy
  if (wasEnabled !== enabled) {
    setWasEnabled(enabled)
    held = enabled && pointerHeld
    setBusy(held)
  }

  // Immer, auch ausgeschaltet: Der Zähler muss den Zeiger schon kennen, BEVOR
  // dieser Hook eingeschaltet wird — sonst wüsste er beim Einschalten nichts.
  useEffect(trackPointer, [])

  useEffect(() => {
    if (!domNode || !enabled) {
      // Beim Abwählen mitten in einer Geste bliebe das Merkmal sonst stehen.
      setBusy(false)
      return
    }
    // Mitten in einer Geste eingeschaltet (siehe `pointerHeld`): Die Leiste
    // bleibt weg, das `pointerup` unten holt sie nach der Frist zurück.
    if (pointerHeld) {
      setBusy(true)
      // Das Loslassen dieser Geste löst die Wartezeit aus, auch wenn ihr
      // `pointerdown` vor dem Einschalten lag.
      gestureRef.current = true
    }

    /*
      Nicht bei einem Klick auf die Leiste selbst. Ihr `stopPropagation` hält
      nur Reacts Weiterleitung auf — React hängt am Wurzelknoten, und dieser
      native Lauscher am `.react-flow` sieht das Ereignis vorher. Ohne die
      Ausnahme blendete sich die Leiste bei ihrem eigenen Klick aus, und kein
      Knopf darauf war erreichbar.
    */
    const onBar = (event: Event) =>
      event.target instanceof Element && event.target.closest('.canvas-options-bar') !== null
    const clearIdle = () => {
      if (idleRef.current) clearTimeout(idleRef.current)
      idleRef.current = null
    }
    const down = (event: Event) => {
      if (onBar(event)) return
      gestureRef.current = true
      clearIdle()
      setBusy(true)
    }
    const up = () => {
      if (rubberBandRef.current) {
        // Nach einem Rahmen sofort — siehe `rubberBandRef`.
        rubberBandRef.current = false
        gestureRef.current = false
        clearIdle()
        setBusy(false)
        return
      }
      if (!gestureRef.current) return
      gestureRef.current = false
      clearIdle()
      setBusy(true)
      idleRef.current = setTimeout(() => setBusy(false), RELEASE_IDLE)
    }

    /*
      Das Fahren des Ausschnitts — am STORE, nicht am Rad.

      Zwei Anläufe am `wheel`-Ereignis sind gescheitert: In der Bubbling-Phase
      kam es nie an (React Flows Pan-Handler verschluckt es am Renderer), in
      der Capture-Phase hörte es JEDES Delta — auch die winzigen, die eine
      Magic Mouse oder ein Trackpad bei bloßem Fingerkontakt liefern. Die
      Leiste verschwand dann beim Versuch, sie anzuklicken. Was zählt, ist
      allein, ob sich der Ausschnitt BEWEGT — und das sagt `transform`.
      Bewegung hat kein Ende, das sie meldet, deshalb die Nachlaufzeit.
    */
    let previous = storeApi.getState().transform
    const unsubscribe = storeApi.subscribe(state => {
      const next = state.transform
      if (next[0] === previous[0] && next[1] === previous[1] && next[2] === previous[2]) return
      previous = next
      setBusy(true)
      clearIdle()
      /*
        **Läuft eine Pointer-Geste, gehört ihr das Ende.** Zieht man einen Zettel
        an den Rand, fährt der Ausschnitt mit (React Flows
        `autoPanOnNodeDrag`, ebenso der **Tauschpunkt** über `useSwapDrag`) —
        und kam er zur Ruhe, während die Hand den Zettel weiter trug, zündete
        diese Nachlaufzeit mitten im Zug: Die Leiste stand wieder da, obwohl
        der Zeiger nie losgelassen wurde. Das `pointerup` setzt danach ohnehin
        seine eigene, längere Frist (`RELEASE_IDLE`).
      */
      if (gestureRef.current) return
      idleRef.current = setTimeout(() => setBusy(false), PAN_IDLE)
    })

    domNode.addEventListener('pointerdown', down)
    // Am `window` und nicht am Canvas: Losgelassen wird oft außerhalb, wenn
    // der Zeiger beim Ziehen über den Rand hinausgeraten ist.
    window.addEventListener('pointerup', up)
    window.addEventListener('pointercancel', up)
    return () => {
      if (idleRef.current) clearTimeout(idleRef.current)
      unsubscribe()
      domNode.removeEventListener('pointerdown', down)
      window.removeEventListener('pointerup', up)
      window.removeEventListener('pointercancel', up)
    }
  }, [domNode, storeApi, enabled])

  // `held` und nicht `busy`: im Durchgang des Umschlags schon der neue Wert.
  return held || rubberBand
}

/**
 * Ob der Zeiger auf einem **Anlegepunkt** ruht und dort die Vorschau steht —
 * dann weicht die **Options-Leiste** (CONTEXT.md, „Sie weicht der Geste").
 *
 * **Der Merker gehört den Punkten und darf sie nicht überleben.** Werden sie
 * abgebaut — Klick auf ein Plus (die Auswahl wandert auf den neuen Zettel),
 * Abwählen, Bearbeiten-Modus —, kommt unter dem Zeiger KEIN `pointerleave`
 * mehr, und ein hängengebliebenes `true` verstellte die Leiste dauerhaft,
 * sobald der Zettel wieder ausgewählt wird (nach `cmd+z`).
 *
 * **Node-lokal, nicht aus `CanvasFlow`:** Dort läge zwar die einzige Wahrheit
 * (eine Vorschau je Canvas), sie käme aber nur über einen Context herunter,
 * dessen Wert bei jedem Überfahren umschlüge — und dann renderte JEDER Zettel
 * neu, nicht nur der ausgewählte. Denselben Handel machen `useSoloSelected`
 * und `useCanvasPointerBusy` weiter oben.
 *
 * Zurückgesetzt wird im Render, nicht im Effekt: Sonst zeigte ein Frame lang
 * noch der alte Wert, und die Leiste käme sichtbar verspätet.
 *
 * @param handlesShown ob die Anlegepunkte gerade gezeigt werden
 * @example
 * const [spawnHovered, setSpawnHovered] = useSpawnHovered(spawnHandlesShown)
 */
export function useSpawnHovered(handlesShown: boolean) {
  const [hovered, setHovered] = useState(false)
  if (hovered && !handlesShown) setHovered(false)
  return [hovered, setHovered] as const
}
